import { MapPin } from 'lucide-react';
import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { mapLinksFor, type Place } from '../lib/maps';

/**
 * *Open in Maps* — one control for one place, TROOP-242.
 *
 * **SPEC-GAP.** `handoff/design-system.md` §6 lists no control that leaves the product, and §7's
 * icon table carries `map-pin` for a location and nothing that opens one. So this borrows the
 * vocabulary rather than inventing any: caption weight, the underline `AnchorLink` uses, `--tap-min`
 * for the target, and the dropdown `AccountMenu` already established.
 *
 * The product used to tell a family the address and stop there, so a parent in a car park on a
 * Friday evening selected plain text, copied it, switched app and pasted. This hands the text an
 * adult typed to the phone's own map app; the map app's Directions button is one tap further and
 * knows where the reader is, which we do not and should not.
 *
 * **Nothing here decides anything.** Which apps to offer and what the href is are
 * `web/src/lib/maps.ts`'s, so this component never sees a user agent — the length of the array is
 * the only thing it branches on. Two per-call-site checks is how one screen offers Apple Maps and
 * another does not, forever, with nothing failing.
 *
 * **No control at all when there is no place**, rather than a disabled one: `Button` requires a
 * `disabledReason` and there is no reason to state. The caller does not have to test for that —
 * this renders nothing.
 *
 * **The choice is a `role="menu"` dropdown and not an `Overlay`**, the distinction the design
 * system draws for `AccountMenu`. It is not that component and does not try to be: that one is the
 * account destination list, and merging a two-item place picker into it would put the header's
 * state machine on every fact row. What is shared is the behaviour a menu owes a keyboard and a
 * thumb — Escape closes, a click outside closes.
 */
export function MapLink({ name, address }: Place) {
  const [open, setOpen] = useState(false);
  const menu = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };

    const onPointerDown = (e: PointerEvent) => {
      if (menu.current && !menu.current.contains(e.target as Node)) setOpen(false);
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [open]);

  const links = mapLinksFor({ name, address });

  if (links.length === 0) {
    return null;
  }

  // Which place this is, for somebody who cannot see which row the control is on. A fact row and a
  // travel leg both put two of these near each other, and "Open in Maps" twice says nothing about
  // which is the camp and which is the car park.
  const which = <span className="sr-only"> — {name?.trim() || address?.trim()}</span>;

  // A one-item menu is a menu nobody wants: everywhere but an Apple platform this is a plain link.
  if (links.length === 1) {
    return (
      <a {...OPENS_A_MAP} href={links[0].href} style={CONTROL} className={CONTROL_CLASS}>
        <MapPin size={14} aria-hidden="true" style={{ flexShrink: 0 }} />
        Open in Maps
        {which}
      </a>
    );
  }

  return (
    <div style={{ position: 'relative' }} ref={menu}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        style={{ ...CONTROL, background: 'none', border: 'none' }}
        className={CONTROL_CLASS}
      >
        <MapPin size={14} aria-hidden="true" style={{ flexShrink: 0 }} />
        Open in Maps
        {which}
      </button>

      {open && (
        <div
          role="menu"
          aria-label="Open in Maps"
          style={{
            position: 'absolute',
            right: 0,
            top: 'calc(100% + 4px)',
            minWidth: '11rem',
            maxWidth: 'calc(100vw - var(--space-5) * 2)',
            background: 'var(--color-surface-raised)',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--elevation-overlay)',
            zIndex: 20,
            overflow: 'hidden',
            paddingBlock: 'var(--space-1)',
          }}
        >
          {links.map((link) => (
            <a
              {...OPENS_A_MAP}
              key={link.app}
              role="menuitem"
              href={link.href}
              onClick={() => setOpen(false)}
              className="t-body flex items-center gap-3"
              style={{
                minHeight: 'var(--tap-min)',
                padding: 'var(--space-2) var(--space-4)',
                color: 'var(--color-text-primary)',
                textDecoration: 'none',
              }}
            >
              <MapPin
                size={16}
                aria-hidden="true"
                style={{ color: 'var(--color-text-muted)', flexShrink: 0 }}
              />
              {link.app}
              {which}
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * A new tab, and no handle back to this one. `rel` is not optional on a `target="_blank"`: the
 * opened page can otherwise reach `window.opener`.
 */
const OPENS_A_MAP = { target: '_blank', rel: 'noopener noreferrer' } as const;

/**
 * Caption weight, not `Button`'s. `BUTTON_BASE` is 48px tall by design and this sits under a fact
 * row's value and on a travel leg's places line — a control the size of the fact it is about would
 * read as the page's main action, which the departure time is.
 */
const CONTROL: CSSProperties = {
  minHeight: 'var(--tap-min)',
  padding: 0,
  color: 'var(--color-text-secondary)',
  textDecoration: 'underline',
  whiteSpace: 'nowrap',
};

const CONTROL_CLASS = 't-caption inline-flex items-center gap-2';
